import React from "react";

const FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type UseDialogA11yOptions = {
    onCancel: () => void;
    initialFocusRef?: React.RefObject<HTMLElement>;
};

type UseDialogA11yResult = {
    dialogRef: React.RefObject<HTMLDivElement>;
    titleId: string;
};

// Wires up the four WCAG behaviors expected of a modal dialog:
// — Escape closes (calls onCancel)
// — Tab/Shift+Tab trap focus inside the dialog
// — Initial focus lands on initialFocusRef (or the first focusable element)
// — Focus is restored to the previously focused element on unmount
export function useDialogA11y({
    onCancel,
    initialFocusRef,
}: UseDialogA11yOptions): UseDialogA11yResult {
    const dialogRef = React.useRef<HTMLDivElement>(null);
    const titleId = React.useId();

    // Hold the latest onCancel in a ref so changing it does not retear the
    // effect (which would otherwise re-fire focus/restore on every render).
    const onCancelRef = React.useRef(onCancel);
    onCancelRef.current = onCancel;

    React.useEffect(() => {
        const previousFocus = document.activeElement as HTMLElement | null;

        const focusTimer = window.setTimeout(() => {
            if (initialFocusRef?.current) {
                initialFocusRef.current.focus();
                return;
            }
            dialogRef.current
                ?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
                ?.focus();
        }, 0);

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onCancelRef.current();
                return;
            }
            if (e.key !== "Tab" || !dialogRef.current) return;

            const focusables = Array.from(
                dialogRef.current.querySelectorAll<HTMLElement>(
                    FOCUSABLE_SELECTOR
                )
            );
            if (focusables.length === 0) return;

            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            window.clearTimeout(focusTimer);
            document.removeEventListener("keydown", handleKeyDown);
            previousFocus?.focus();
        };
        // initialFocusRef is a ref object (stable identity across renders).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { dialogRef, titleId };
}
